import {
  Form as RemixForm,
  useActionData,
  ActionFunction,
  Link,
} from "react-router";
import { signupUser } from "~/utils/auth.server";
import { Card, Text, Button, Flex, Heading, TextField } from "@radix-ui/themes";
import * as Form from "@radix-ui/react-form";

type ActionData = {
  errors?: {
    general?: string;
    email?: string;
    username?: string;
    password1?: string;
    password2?: string;
  };
};

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const password1 = formData.get("password1") as string;
  const password2 = formData.get("password2") as string;

  if (!email || !password1 || !password2) {
    return { errors: { general: "All fields are required" } };
  }

  if (password1 !== password2) {
    return { errors: { password2: "Passwords do not match" } };
  }

  try {
    return await signupUser(email, password1, password2);
  } catch (error: unknown) {
    // Return detailed errors if available
    if (error && typeof error === "object" && "details" in error) {
      return { errors: error.details as Record<string, string> };
    }
    // Fallback to general error
    const message = error instanceof Error ? error.message : "Signup failed";
    return { errors: { general: message } };
  }
};

export default function Signup() {
  const actionData = useActionData<ActionData>();

  return (
    <Flex align="center" justify="center" className="h-screen">
      <Card variant="surface" className="max-w-sm w-full shadow-lg p-6">
        <Heading size="6" weight="bold" mb="4">
          Sign Up
        </Heading>
        {actionData?.errors?.general && (
          <Text color="ruby" size="2" mb="4">
            {actionData.errors.general}
          </Text>
        )}

        <Form.Root asChild>
          <RemixForm method="post" action="/signup" autoComplete="off">
            <Form.Field name="email" className="mb-4">
              <Form.Label asChild>
                <Text as="label" size="1">
                  Email
                </Text>
              </Form.Label>
              <Form.Control asChild>
                <TextField.Root
                  placeholder="Enter your email"
                  type="email"
                  required
                  autoComplete="off"
                />
              </Form.Control>
              {(actionData?.errors?.email || actionData?.errors?.username) && (
                <Text size="1" color="ruby">
                  {actionData.errors.email || actionData.errors.username}
                </Text>
              )}
              <Form.Message asChild match="valueMissing">
                <Text size="1" color="ruby">
                  Email is required
                </Text>
              </Form.Message>
              <Form.Message asChild match="typeMismatch">
                <Text size="1" color="ruby">
                  Please enter a valid email address
                </Text>
              </Form.Message>
            </Form.Field>

            <Form.Field name="password1" className="mb-4">
              <Form.Label asChild>
                <Text as="label" size="1">
                  Password
                </Text>
              </Form.Label>
              <Form.Control asChild>
                <TextField.Root
                  placeholder="Enter your password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </Form.Control>
              {actionData?.errors?.password1 && (
                <Text size="1" color="ruby">
                  {actionData.errors.password1}
                </Text>
              )}
              <Form.Message match="valueMissing">
                <Text size="1" color="ruby">
                  Password is required
                </Text>
              </Form.Message>
              <Form.Message match="tooShort">
                <Text size="1" color="ruby">
                  Password must be at least 8 characters
                </Text>
              </Form.Message>
            </Form.Field>

            <Form.Field name="password2" className="mb-4">
              <Form.Label asChild>
                <Text as="label" size="1">
                  Confirm Password
                </Text>
              </Form.Label>
              <Form.Control asChild>
                <TextField.Root
                  placeholder="Confirm your password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </Form.Control>
              {actionData?.errors?.password2 && (
                <Text size="1" color="ruby">
                  {actionData.errors.password2}
                </Text>
              )}
              <Form.Message match="valueMissing">
                <Text size="1" color="ruby">
                  Please confirm your password
                </Text>
              </Form.Message>
            </Form.Field>

            <Form.Submit asChild>
              <Button className="w-full mb-4">Sign Up</Button>
            </Form.Submit>
          </RemixForm>
        </Form.Root>

        <Text size="1" align="center">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:underline">
            Login here
          </Link>
        </Text>
      </Card>
    </Flex>
  );
}
